/**
 * Utility methods for managing Course Materials
 */
import _ from 'lodash';

import type CourseMaterial from 'bundles/author-course/models/CourseMaterial';
import type Lesson from 'bundles/author-course/models/Lesson';
import type Module from 'bundles/author-course/models/Module';
import CourseMaterialUtils from 'bundles/author-course/utils/CourseMaterialUtils';

const SPECIAL_CHAR_REGEX = /^[\s/,!\\^${}[\]().*+?|<>&\-~`@#%_=:;'"\p{Emoji_Presentation}]*$/u;

export const isSlugifiable = (name: string) => {
  // Test if name contains only frequently used special characters (and spaces)
  return !SPECIAL_CHAR_REGEX.test(name);
};

export const moduleValidators = {
  hasName(module: Module) {
    return module.name.length > 0;
  },

  isNameSlugifiable(module: Module) {
    return isSlugifiable(module.name);
  },

  /**
   * Honors lessons must appear at the end of the module, after all core lessons.
   */
  isTrackOrderValid(module: Module) {
    const lessons = module.elements;

    const honorsLessons = CourseMaterialUtils.getHonorsTrackLessons(lessons);
    const lastCoreLessonIndex = _.findLastIndex(lessons, (lesson) => CourseMaterialUtils.isCoreLesson(lesson));

    return honorsLessons.every((honorsLesson) => {
      const honorsLessonIndex = _.findIndex(lessons, (lesson) => lesson.id === honorsLesson.id);
      return honorsLessonIndex > lastCoreLessonIndex;
    });
  },
};

export const isValidLesson = (lesson: Lesson) => {
  const hasName = lesson.name.length > 0;
  const isSlugifiableBool = isSlugifiable(lesson.name);
  const hasGradedItemIfHonors = !lesson.isHonors || lesson.hasGradedItem;

  return hasName && isSlugifiableBool && hasGradedItemIfHonors;
};

export const areValidLessons = (lessons: Array<Lesson>) => {
  return lessons.every(isValidLesson);
};

export const isValidModule = (module: Module) => {
  return !_.findKey(moduleValidators, (validator) => !validator(module)) && areValidLessons(module.elements);
};

export const areValidModules = (modules: Array<Module>) => {
  return modules.every(isValidModule);
};

export const hasValidationErrors = (courseMaterial: CourseMaterial) => {
  return !areValidModules(courseMaterial.elements);
};

export const validateCourseMaterialSessionSupport = (courseMaterial: CourseMaterial) => {
  return !!courseMaterial && !!courseMaterial.elements && courseMaterial.items.find((item) => item.published);
};
